import type {
  ApiCapability,
  ApiKeyGrantInput,
  CreateGenerationProjectInput,
  DestinationDto,
  GenerationConnectorAccounting,
  GenerationConnectorContract,
  GenerationConnectorSlot,
  GenerationFollowUp,
  GenerationGoal,
  GenerationOptions,
  GenerationRouteStep,
  PreviewRouteInput,
  SaveOptionPresetInput,
  GenerationProjectPublicationDto,
  GenerationProjectSummaryDto,
  GenerationStatsDto,
  JobDto,
  PageDto,
  SourcePostDto,
} from '@mpr/shared';

/**
 * Thin API client.
 *
 * `credentials: 'include'` on every call because the session lives in an
 * httpOnly cookie — the dashboard never holds a token in JavaScript, which is
 * also why nothing sensitive can end up in localStorage or a stack trace here.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly hint: string | undefined;
  readonly reason: string | undefined;
  /**
   * Meta refused free text because the 24-hour window has closed.
   *
   * Carried as its own flag rather than left to be recognised from the message,
   * because it is the one refusal that has a legitimate next step — a utility
   * template — and the screen has to be able to offer it without matching on
   * prose that could be translated or reworded.
   */
  readonly windowClosed: boolean;

  constructor(
    status: number,
    message: string,
    hint?: string,
    reason?: string,
    windowClosed = false,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.hint = hint;
    this.reason = reason;
    this.windowClosed = windowClosed;
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    credentials: 'include',
    headers:
      init.body && !(init.body instanceof FormData)
        ? { 'content-type': 'application/json', ...init.headers }
        : init.headers,
    ...init,
  });

  const text = await response.text();

  /*
   * A body that is not JSON is still an answer, and it has to arrive as one.
   *
   * Everything this API returns is JSON, so anything else came from in front
   * of it: a gateway timeout page, a proxy's 502, a login wall. Parsed
   * optimistically, that threw a SyntaxError — which is not an ApiError, so
   * every screen fell through to its "request failed" fallback and the status
   * that would have explained it was lost. An operator reporting "it says the
   * request failed" could not be told the difference between a refusal and a
   * gateway.
   */
  let body: Record<string, unknown>;
  try {
    body = text ? (JSON.parse(text) as Record<string, unknown>) : {};
  } catch {
    throw new ApiError(
      response.status,
      `The server returned an unexpected response (${response.status})`,
      // The first line of it, which is usually the gateway naming itself.
      text.trim().slice(0, 120) || undefined,
      'NOT_JSON',
    );
  }

  if (!response.ok) {
    throw new ApiError(
      response.status,
      (body.error as string) ?? `Request failed (${response.status})`,
      body.hint as string | undefined,
      body.reason as string | undefined,
      body.windowClosed === true,
    );
  }

  return body as T;
}

/**
 * A plan as the console edits it: limits, entitlements and pricing together.
 * Spelled out rather than left open, because the plan editor writes every one
 * of these fields and a loose type would let a rename slip through silently.
 */
/**
 * A plan, with its entitlements and limits as key maps rather than columns.
 *
 * Open-ended on purpose: features and limits are catalogue rows an
 * administrator can add, so the console renders whatever the catalogue returns
 * instead of a list compiled into this file.
 */
/** A social network's key: LINKEDIN, or any network defined as data. */
export type SocialProviderId = string;

export interface SocialProviderInfo {
  provider: SocialProviderId;
  displayName: string;
  configured: boolean;
  redirectUri: string;
  /** What the network's app form asks for, from its definition. */
  settings: Array<{
    name: string;
    label: string;
    type: 'boolean' | 'string';
    default?: boolean | string;
    options?: string[];
  }>;
  defaultApiVersion: string | null;
  /** 'token': connected by naming places for a pasted token (a Telegram bot), not by a login. */
  authType: 'oauth2' | 'token';
  clientIdLabel?: string;
  clientSecretLabel?: string;
  accountsLabel?: string;
}

export interface SocialNetworkRow {
  key: string;
  name: string;
  status: 'ACTIVE' | 'DISABLED';
  source: 'PRESET' | 'OVERRIDE' | 'CUSTOM';
  verifiedLive: boolean;
  contract: unknown;
}

export interface SocialConnectionRow {
  id: string;
  provider: SocialProviderId;
  displayName: string;
  status: string;
  grantedScopes: string[];
  tokenExpiresAt: string | null;
  canRefresh: boolean;
  lastVerifiedAt: string | null;
  invalidationReason: string | null;
  accounts: Array<{
    id: string;
    name: string;
    kind: 'PERSON' | 'ORGANIZATION' | 'ACCOUNT';
    isActive: boolean;
  }>;
}

export interface SocialAppRow {
  id: string;
  provider: SocialProviderId;
  label: string;
  scope: 'PLATFORM' | 'TENANT';
  clientId: string;
  secretFingerprint: string | null;
  apiVersion: string | null;
  settings: Record<string, boolean | string>;
  status: 'DRAFT' | 'ACTIVE' | 'INVALID' | 'DISABLED';
  isDefault: boolean;
  connectionCount: number;
  redirectUri: string;
  createdAt: string;
}

export interface AdminPlan {
  id: string;
  code: string;
  name: string;
  priceMonthly: number | null;
  currency: string;
  sortOrder: number;
  isActive: boolean;
  tenantCount: number;
  /** Feature key → sold by this plan. */
  features: Record<string, boolean>;
  /** Quota key → limit. `null` is unlimited. */
  quotas: Record<string, number | null>;
}

/**
 * Where one Meta permission stands.
 *
 * `PUBLISHED` is the only status a customer is asked for; the other two are
 * offered to platform staff and Meta's reviewer alone, because asking a
 * customer for an unapproved permission can make Meta refuse the whole login
 * dialog.
 */
export type MetaPermissionStatus = 'IN_DEVELOPMENT' | 'PENDING_APPROVAL' | 'PUBLISHED';

export interface MetaPermissionDto {
  id: string;
  scope: string;
  channel: string | null;
  status: MetaPermissionStatus;
  note: string | null;
  /** Last time a login by somebody outside the platform came back holding it. */
  lastGrantedToCustomerAt: string | null;
  sortOrder: number;
}

export interface PageBinding {
  pageId: string;
  name: string;
  externalId: string;
  /** False when Meta did not grant MODERATE on this Page. */
  canComment: boolean;
  webhookSubscribed: boolean;
  binding: {
    agentConfigId: string;
    autoReplyComments: boolean;
    autoPublishReplies: boolean;
    autoDraftInbox: boolean;
    minCommentLength: number;
    maxRepliesPerHour: number;
  } | null;
}

export interface SmtpSummary {
  configured: boolean;
  source: 'database' | 'environment' | 'none';
  host: string | null;
  port: number;
  username: string | null;
  fromAddress: string | null;
  fromName: string;
  /** Whether a password is stored — never the password itself. */
  hasPassword: boolean;
  isActive: boolean;
  lastTestedAt: string | null;
  lastTestError: string | null;
  lastTestTo: string | null;
}

export interface SignupRequest {
  id: string;
  organisationName: string;
  contactName: string;
  email: string;
  phone: string;
  emailVerifiedAt: string | null;
  status: 'PENDING' | 'UNVERIFIED' | 'APPROVED' | 'REJECTED';
  reviewNote: string | null;
  reviewedAt: string | null;
  createdAt: string;
}

export interface CatalogueFeature {
  id: string;
  key: string;
  name: string;
  description: string | null;
  capabilityKey: string | null;
  status: 'AVAILABLE' | 'COMING_SOON' | 'RETIRED';
  sortOrder: number;
  isSystem: boolean;
}

export interface CatalogueQuota {
  id: string;
  key: string;
  name: string;
  description: string | null;
  period: 'TOTAL' | 'DAY' | 'MONTH';
  sortOrder: number;
  isSystem: boolean;
}

export interface QuotaUsage {
  key: string;
  name: string;
  period: 'TOTAL' | 'DAY' | 'MONTH';
  /** `null` is unlimited — render it as such, never as zero. */
  limit: number | null;
  used: number;
  remaining: number | null;
  exceeded: boolean;
}

export interface FeatureState {
  key: string;
  name: string;
  enabled: boolean;
  blockedBy: 'CAPABILITY' | 'ENTITLEMENT' | 'SETTING' | 'UNRELEASED' | null;
  capability: boolean;
  entitlement: boolean;
  setting: boolean;
  status: 'AVAILABLE' | 'COMING_SOON' | 'RETIRED';
  reason: string | null;
}

export interface InsightsReport {
  range: { from: string; to: string; days: number };
  totals: {
    posts: number;
    comments: number;
    reactions: number;
    shares: number;
    reach: number | null;
  };
  previous: {
    posts: number;
    comments: number;
    reactions: number;
    shares: number;
    reach: number | null;
  };
  change: {
    posts: number | null;
    comments: number | null;
    reactions: number | null;
    shares: number | null;
    reach: number | null;
  };
  series: Array<{
    day: string;
    posts: number;
    comments: number;
    reactions: number;
  }>;
  byPageSeries: {
    pages: Array<{ pageId: string; name: string }>;
    days: Array<{
      day: string;
      posts: number[];
      comments: number[];
      reactions: number[];
    }>;
  };
  pages: Array<{
    pageId: string;
    name: string;
    posts: number;
    comments: number;
    reactions: number;
  }>;
  topPosts: Array<{
    externalPostId: string;
    pageName: string;
    publishedAt: string;
    excerpt: string | null;
    permalink: string | null;
    comments: number;
    reactions: number;
  }>;
  collectedAt: string | null;
  backfilling: boolean;
}

export interface PageProfile {
  id: string;
  name: string;
  about: string | null;
  description: string | null;
  category: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  address: string | null;
  link: string | null;
  followers: number | null;
  pictureUrl: string | null;
  coverUrl: string | null;
  hours: Array<{ day: string; open: string | null; close: string | null }>;
  location: {
    street: string | null;
    city: string | null;
    state: string | null;
    country: string | null;
    zip: string | null;
    latitude: number | null;
    longitude: number | null;
  };
}

export interface InboxSuggestion {
  id: string;
  reply: string | null;
  /** What the agent said when it declined. Not copy for a customer. */
  declineReason: string | null;
  outcome: string;
  confidence: number | null;
  durationMs: number | null;
  createdAt: string;
  /**
   * Which of the two agents wrote it.
   *
   * `assistant` is the one bound to the Page, which knows its voice and rules.
   * `agent` is somebody's own software reaching in through an API key. They
   * produce the same kind of draft and are trusted differently, so a person
   * choosing between two of them has to be told which is which.
   */
  source: 'assistant' | 'agent';
  /** The assistant's name, or the key's. */
  sourceName: string | null;
  /**
   * Why no draft came back, on the one call that just asked for it.
   *
   * Present only on the answer to `suggestInboxReply` — a draft read back from
   * a list is a stored row and carries no reason. Optional for that reason,
   * and never to be relied on as a record.
   */
  error?: string | null;
}

export interface InboxRow {
  id: string;
  channel: string;
  /** The exchange this belongs to, so a row can be opened as a conversation. */
  conversationId: string | null;
  pageId: string;
  pageName: string;
  externalPostId: string;
  externalCommentId: string;
  postExcerpt: string | null;
  authorName: string | null;
  message: string | null;
  createdAt: string;
  repliedAt: string | null;
  /** Set aside as needing no answer. */
  dismissedAt: string | null;
  /** Answered by somebody else, outside this system. */
  handledExternally: boolean;
  suggestions: InboxSuggestion[];
}

export interface CommentThread {
  externalPostId: string;
  postExcerpt: string | null;
  total: number;
  waiting: number;
  latestAt: string;
  comments: InboxRow[];
}

/** One utility message template, with Meta's current verdict on it. */
export interface UtilityTemplate {
  id: string;
  name: string;
  language: string;
  category: string;
  /** APPROVED, PENDING, REJECTED — Meta's word, not ours. */
  status: string;
  rejectionReason: string | null;
  components: unknown;
}

/** One WhatsApp Cloud API number, as the numbers screen needs it. */
export interface WhatsappNumber {
  id: string;
  wabaId: string;
  wabaName: string | null;
  phoneNumberId: string;
  displayNumber: string;
  verifiedName: string | null;
  /** Still live in the WhatsApp Business app on a phone, as well as here. */
  coexistence: boolean;
  pageId: string | null;
  /** Null means Meta is not delivering to us — messages are being lost, not queued. */
  subscribedAt: string | null;
  /** Set when Meta last refused this number's stored token. */
  credentialsInvalidAt: string | null;
  isActive: boolean;
  page: { id: string; name: string } | null;
}

/** What a generation connector talks to, and how. */
export type GenerationCapability = 'SCRIPT' | 'AUDIO' | 'VIDEO' | 'IMAGE';

/**
 * A connector row. The contract fields (auth style, request template, response
 * path…) are what make it generic: the API never branches on the provider.
 */
export interface GenerationConnector
  extends GenerationConnectorContract,
    GenerationConnectorSlot,
    GenerationConnectorAccounting {
  id: string;
  name: string;
  capability: GenerationCapability;
  provider: string;
  endpointUrl: string;
  isDefault: boolean;
  isActive: boolean;
  /** Never the token itself — only whether one is stored. */
  hasAuthToken: boolean;
  /** The GENERATION_PRESETS entry this row started from; informational only. */
  presetId: string | null;
  /** Only a PASSED connector is used by real jobs; any contract edit resets it to UNTESTED. */
  verificationStatus: GenerationConnectorVerification;
  lastTestedAt: string | null;
  lastTestError: string | null;
  createdAt: string;
  updatedAt: string;
}

export type GenerationConnectorVerification = 'UNTESTED' | 'PASSED' | 'FAILED';

/** The latest test run of a connector, and what came of it. */
export interface GenerationConnectorTest {
  jobId: string;
  status: 'PENDING' | 'RUNNING' | 'DONE' | 'FAILED';
  running: boolean;
  prompt: string | null;
  startedAt: string;
  finishedAt: string | null;
  resultText: string | null;
  resultMediaId: string | null;
  resultUrl: string | null;
  resultMimeType: string | null;
  errorMessage: string | null;
}

export interface GenerationConnectorTestState {
  connector: GenerationConnector;
  test: GenerationConnectorTest | null;
  /** The provider's reply to the last finished test, scrubbed; pretty JSON when it was JSON. */
  providerResponse: string | null;
  /** The request that test sent, scrubbed (JSON text): method, URL without credentials, header names, body. */
  providerRequest?: string | null;
}

/** What POST …/repair answers: a proposal to show and apply. The server saves nothing. */
export interface GenerationConnectorRepair {
  /** Arabic, one to three sentences: what was wrong. */
  explanation: string;
  confidence: 'high' | 'medium' | 'low';
  /** Only contract fields, complete new values; already checked against the server's rules. */
  patch: Record<string, unknown>;
  /** Parts of the model's answer that were not allowed or did not validate, with why. */
  dropped: Array<{ field: string; reason: string }>;
  /** The name of the text connector that wrote the proposal. */
  model: string;
}

/**
 * Create body. Nullable text is sent as `null`, never as an empty string.
 * Carries every contract field, including the polling trio (`pollPath`,
 * `pollUrlTemplate`, `pollDonePath`) and `authOnResultFetch`.
 */
export interface GenerationConnectorInput
  extends GenerationConnectorContract,
    Partial<GenerationConnectorSlot>,
    Partial<GenerationConnectorAccounting> {
  name: string;
  capability: GenerationCapability;
  provider: string;
  endpointUrl: string;
  authToken?: string;
  isDefault?: boolean;
  presetId: string | null;
}

export type GenerationJobStatus = 'PENDING' | 'RUNNING' | 'DONE' | 'FAILED' | 'APPROVED';

export type GenerationJobStage = 'POST_TEXT' | 'MEDIA_PROMPT' | 'MEDIA';

/** A saved set of choices ("Saudi reels, male voice"). */
export interface GenerationOptionPreset {
  id: string;
  name: string;
  goal: GenerationGoal;
  followUp: GenerationFollowUp;
  options: GenerationOptions;
  createdAt: string;
}

/** What `produce` started: one media job, or a plan for a long video. */
export interface GenerationProduceResult {
  job?: GenerationJob;
  plan?: VideoPlan;
}

/**
 * One request to a generation connector and its outcome.
 *
 * `resultMediaId` is a MediaAsset id, not a URL — this contract has no public
 * address for it yet. Playing or downloading it goes through
 * `mediaAssetUrl()` below, which is a placeholder until the backend's
 * download route is confirmed.
 */
export interface GenerationJob {
  id: string;
  capability: GenerationCapability;
  status: GenerationJobStatus;
  parentJobId: string | null;
  /** The studio project this belongs to; null only for records from before projects existed. */
  projectId: string | null;
  /** What a text job is for (POST_TEXT, IMAGE_PROMPT…); null for jobs made outside a goal. */
  purpose: string | null;
  /** Which part of a goal project it is: the post, the media prompt, or the media itself. */
  stage: GenerationJobStage | null;
  /** The choices the job was made with. */
  options: GenerationOptions | null;
  promptText: string | null;
  referenceMediaIds: string[];
  audioMediaId: string | null;
  resultText: string | null;
  resultMediaId: string | null;
  feedbackText: string | null;
  errorMessage: string | null;
  /** Estimated USD at the connector's prices; null (never 0) when no price was known or it is not finished. */
  costUsd?: number | null;
  priceKnown?: boolean;
  createdAt: string;
  updatedAt: string;
}

export type VideoPlanStatus = 'DRAFT' | 'RUNNING' | 'PAUSED' | 'DONE' | 'FAILED';

/**
 * One shot of a longer video: a visual description in English (what the
 * camera sees), the script lines it covers, and the VIDEO job that made it.
 */
export interface VideoShot {
  id: string;
  position: number;
  durationSeconds: number;
  scriptExcerpt: string;
  visualPrompt: string;
  status: GenerationJobStatus;
  jobId: string | null;
  resultMediaId: string | null;
  errorMessage: string | null;
}

/**
 * A script cut into shots and run as a chain of VIDEO jobs. `resultMediaId`
 * is the video so far (each extension returns the whole film to date), so
 * it is the final video once the plan is DONE.
 */
export interface VideoPlan {
  id: string;
  status: VideoPlanStatus;
  scriptText: string;
  scriptJobId: string | null;
  /** The studio project this belongs to; null only for records from before projects existed. */
  projectId: string | null;
  pauseBetweenShots: boolean;
  referenceMediaIds: string[];
  resultMediaId: string | null;
  resultUrl: string | null;
  errorMessage: string | null;
  totalSeconds: number;
  /** The sum of its shots' jobs; null when none has a price. */
  costUsd?: number | null;
  priceKnown?: boolean;
  shots: VideoShot[];
  createdAt: string;
  updatedAt: string;
}

export interface VideoPlanShotInput {
  scriptExcerpt: string;
  visualPrompt: string;
}

/**
 * One studio project in full: the request, every job made from it (revisions
 * included, flat — `parentJobId` links them), its video plans, and where its
 * output was published.
 */
export type GenerationProject = GenerationProjectSummaryDto & {
  referenceMediaIds: string[];
  jobs: GenerationJob[];
  plans: VideoPlan[];
  publications: GenerationProjectPublicationDto[];
};

/** One continuous exchange with one person, as the side list needs it. */
export interface ConversationDto {
  id: string;
  channel: string;
  pageId: string;
  pageName: string;
  participantName: string | null;
  externalPostId: string | null;
  postExcerpt: string | null;
  lastMessageAt: string;
  lastMessage: string | null;
  lastFromPage: boolean;
  /** Null when nobody is waiting on us. */
  waitingSince: string | null;
}

export const api = {
  me: () =>
    call<{
      user: { id: string; email: string; displayName: string; role: string };
      /**
       * What this role may do, decided by the server's own table.
       *
       * Sent rather than worked out here: a second copy of the role table in the
       * browser drifts from the real one, and it drifts silently in the
       * direction that matters — a tab that stays visible after the capability
       * behind it is gone, answering 403 to everyone who presses it.
       */
      capabilities: string[];
      /**
       * Which channels this installation offers at all — a different question
       * from what this person may do. Sent for the same reason: the browser
       * working it out for itself means a second copy of the answer, and this
       * one is decided by what Meta approved for the app.
       */
      channels: string[];
      /** Which deployment and which commit answered — see BuildBadge. */
      build: {
        environment: string;
        isProduction: boolean;
        commit: string | null;
        builtAt: string | null;
      };
    }>('/api/auth/me'),

  login: (email: string, password: string) =>
    call<{ user: { id: string; displayName: string; role: string } }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  logout: () => call<{ ok: boolean }>('/api/auth/logout', { method: 'POST' }),

  connections: () =>
    call<{
      /** How many Meta accounts this plan allows, and how many are in use. */
      metaAccounts: { used: number; limit: number | null };
      connections: Array<{
        id: string;
        metaUserName: string;
        status: string;
        grantedScopes: string[];
        declinedScopes: string[];
        missingScopes: string[];
        pendingScopes: string[];
        tokenExpiresAt: string | null;
        tokenPresent: boolean;
        lastVerifiedAt: string | null;
        invalidationReason: string | null;
        pageCount: number;
      }>;
      requestedScopes: string[];
      graphVersion: string;
      publicPageSourceEnabled: boolean;
    }>('/api/connections'),

  startOAuth: () =>
    call<{ authorizationUrl: string }>('/api/connections/start', {
      method: 'POST',
    }),

  verifyConnection: (id: string) =>
    call<{ valid: boolean; reason: string | null; expiresAt: string | null }>(
      `/api/connections/${id}/verify`,
      { method: 'POST' },
    ),

  disconnect: (id: string) => call<{ ok: boolean }>(`/api/connections/${id}`, { method: 'DELETE' }),

  /** Every Page and social account this person may use, with what each can do. */
  destinations: () => call<{ destinations: DestinationDto[] }>('/api/destinations'),

  /** A social account's posts: published from here, and read from the network where allowed. */
  socialAccountPosts: (accountId: string) =>
    call<{
      posts: Array<{
        id: string;
        text: string | null;
        createdAt: string;
        permalink: string;
        origin: 'PLATFORM' | 'NETWORK';
      }>;
      networkReadable: boolean;
    }>(`/api/social/accounts/${accountId}/posts`),

  editSocialPost: (accountId: string, postId: string, text: string) =>
    call<{ ok: boolean }>(
      `/api/social/accounts/${accountId}/posts/${encodeURIComponent(postId)}`,
      { method: 'PUT', body: JSON.stringify({ text }) },
    ),

  deleteSocialPost: (accountId: string, postId: string) =>
    call<{ ok: boolean }>(
      `/api/social/accounts/${accountId}/posts/${encodeURIComponent(postId)}`,
      { method: 'DELETE' },
    ),

  socialNetworks: () =>
    call<{
      networks: SocialNetworkRow[];
      presets: Array<{ key: string; name: string; verifiedLive: boolean }>;
    }>('/api/social/networks'),

  validateSocialNetwork: (contract: unknown) =>
    call<{ ok: boolean }>('/api/social/networks/validate', {
      method: 'POST',
      body: JSON.stringify({ contract }),
    }),

  saveSocialNetwork: (key: string, input: { name: string; contract: unknown }) =>
    call<{ ok: boolean }>(`/api/social/networks/${encodeURIComponent(key)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),

  setSocialNetworkStatus: (key: string, status: 'ACTIVE' | 'DISABLED') =>
    call<{ ok: boolean }>(`/api/social/networks/${encodeURIComponent(key)}/status`, {
      method: 'POST',
      body: JSON.stringify({ status }),
    }),

  deleteSocialNetwork: (key: string) =>
    call<{ result: 'RESET' | 'DELETED' }>(`/api/social/networks/${encodeURIComponent(key)}`, {
      method: 'DELETE',
    }),

  socialConnections: () =>
    call<{
      connections: SocialConnectionRow[];
      providers: SocialProviderInfo[];
    }>('/api/social/connections'),

  startSocialOAuth: (provider: SocialProviderId) =>
    call<{ authorizationUrl: string }>('/api/social/connections/start', {
      method: 'POST',
      body: JSON.stringify({ provider }),
    }),

  connectSocialDirect: (input: { provider: SocialProviderId; accountIds: string[] }) =>
    call<{ connectionId: string; displayName: string; accountsDiscovered: number }>(
      '/api/social/connections/direct',
      { method: 'POST', body: JSON.stringify(input) },
    ),

  verifySocialConnection: (id: string) =>
    call<{ valid: boolean; reason: string | null }>(`/api/social/connections/${id}/verify`, {
      method: 'POST',
    }),

  disconnectSocial: (id: string) =>
    call<{ ok: boolean }>(`/api/social/connections/${id}`, { method: 'DELETE' }),

  socialApps: () => call<{ apps: SocialAppRow[] }>('/api/social/apps'),

  createSocialApp: (input: {
    provider: SocialProviderId;
    scope: 'PLATFORM' | 'TENANT';
    label: string;
    clientId: string;
    clientSecret: string;
    apiVersion?: string;
    settings?: Record<string, boolean | string>;
  }) => call<SocialAppRow>('/api/social/apps', { method: 'POST', body: JSON.stringify(input) }),

  updateSocialApp: (
    id: string,
    input: {
      label?: string;
      clientSecret?: string;
      apiVersion?: string;
      settings?: Record<string, boolean | string>;
      status?: 'ACTIVE' | 'DISABLED';
    },
  ) => call<SocialAppRow>(`/api/social/apps/${id}`, { method: 'PUT', body: JSON.stringify(input) }),

  deleteSocialApp: (id: string) => call<void>(`/api/social/apps/${id}`, { method: 'DELETE' }),

  insightsReport: (params: { from?: string; to?: string; pageId?: string }) => {
    const query = new URLSearchParams();
    if (params.from) query.set('from', params.from);
    if (params.to) query.set('to', params.to);
    if (params.pageId) query.set('pageId', params.pageId);
    return call<InsightsReport>(`/api/insights/report?${query.toString()}`);
  },

  collectionStatus: () =>
    call<{
      total: number;
      done: number;
      percent: number;
      collectedAt: string | null;
    }>('/api/insights/collection-status'),

  refreshInsights: () =>
    call<{ pagesRead: number; pagesFailed: number; postsSeen: number }>('/api/insights/refresh', {
      method: 'POST',
    }),

  overview: (refresh = false) =>
    call<{
      activePages: number;
      postsLast7Days: number | null;
      comments: number | null;
      /** When each post of the last eight days was made, for the weekly bars. */
      recentPostTimes: string[];
      queued: number;
      waitingReplies: number;
      refreshedAt: string | null;
      unreadablePages: number;
      revokedPages: Array<{ pageId: string; name: string }>;
    }>(`/api/insights/overview${refresh ? '?refresh=1' : ''}`),

  pages: () => call<{ pages: PageDto[] }>('/api/pages'),

  inbox: (
    params: { pageId?: string; status?: string; q?: string; before?: string; limit?: number } = {},
  ) => {
    const query = new URLSearchParams();
    if (params.pageId) query.set('pageId', params.pageId);
    if (params.status) query.set('status', params.status);
    if (params.q) query.set('q', params.q);
    // A cursor, not an offset: the list is ordered by a moving clock, and an
    // offset would skip or repeat a row whenever something new arrived between
    // one page and the next.
    if (params.before) query.set('before', params.before);
    if (params.limit) query.set('limit', String(params.limit));
    return call<{ items: InboxRow[]; waiting: number; nextBefore: string | null }>(
      `/api/inbox?${query.toString()}`,
    );
  },

  commentThreads: (pageId: string) =>
    call<{ threads: CommentThread[] }>(`/api/pages/${pageId}/comment-threads`),

  messageTemplates: (pageId: string) =>
    call<{ templates: UtilityTemplate[] }>(`/api/pages/${pageId}/message-templates`),

  createMessageTemplate: (
    pageId: string,
    input: {
      name: string;
      language: string;
      bodyText: string;
      bodyExamples: string[];
      headerText?: string | null;
    },
  ) =>
    call<{ id: string; status: string; rejectionReason: string | null }>(
      `/api/pages/${pageId}/message-templates`,
      { method: 'POST', body: JSON.stringify(input) },
    ),

  deleteMessageTemplate: (pageId: string, name: string) =>
    call<{ ok: boolean }>(`/api/pages/${pageId}/message-templates/${encodeURIComponent(name)}`, {
      method: 'DELETE',
    }),

  sendUtilityMessage: (
    id: string,
    input: { templateName: string; language: string; bodyParams: string[] },
  ) =>
    call<{ ok: boolean }>(`/api/inbox/${id}/utility`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /**
   * What this system believes is working, counted from stored state.
   *
   * Deliberately not a live check against Meta — it sits on the home screen and
   * a dozen round trips there would be felt. The per-screen verify actions are
   * what re-ask Meta; this reports how many things are waiting to be re-asked.
   */
  platformHealth: () =>
    call<{
      channels: Array<{
        key: 'FACEBOOK' | 'MESSENGER' | 'INSTAGRAM_DM' | 'WHATSAPP';
        possible: number;
        working: number;
        blocker: 'NONE' | 'DISABLED' | 'NOT_GRANTED' | 'PENDING_APPROVAL' | 'SETUP';
      }>;
      livePages: number;
      activePages: number;
      unverifiedNumbers: number;
      expiringConnections: Array<{
        id: string;
        label: string;
        expiresAt: string;
        daysLeft: number;
        pages: number;
      }>;
    }>('/api/health/platform'),

  subscribeAllWebhooks: () =>
    call<{
      subscribed: number;
      alreadyOn: number;
      skipped: number;
      failed: Array<{ name: string; reason: string }>;
    }>('/api/pages/webhooks', { method: 'POST' }),

  verifyWhatsapp: (id: string) =>
    call<{ subscribed: boolean; credentialsValid: boolean; problem: string | null }>(
      `/api/whatsapp/${id}/verify`,
      { method: 'POST' },
    ),

  whatsappSignupConfig: () =>
    call<{
      appId: string;
      configId: string;
      /** Which layer answered: this tenant's own setting, the Meta app's, or none. */
      source: 'tenant' | 'app' | 'none';
      canEdit: boolean;
    }>('/api/whatsapp/signup-config'),

  setWhatsappSignupConfig: (configId: string) =>
    call<{ configId: string | null }>('/api/whatsapp/signup-config', {
      method: 'PUT',
      body: JSON.stringify({ configId }),
    }),

  /** The code expires in thirty seconds, so this is called the instant the window closes. */
  completeWhatsappSignup: (input: { code: string; wabaId: string; phoneNumberId: string }) =>
    call<{ displayNumber: string; coexistence: boolean }>('/api/whatsapp/signup', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  whatsappNumbers: () => call<{ numbers: WhatsappNumber[] }>('/api/whatsapp/numbers'),

  importWhatsapp: (accessToken: string) =>
    call<{ numbers: number; accounts: number; unreadable: string[] }>('/api/whatsapp/import', {
      method: 'POST',
      body: JSON.stringify({ accessToken }),
    }),

  assignWhatsappPage: (id: string, pageId: string | null) =>
    call<{ ok: boolean }>(`/api/whatsapp/${id}/page`, {
      method: 'POST',
      body: JSON.stringify({ pageId }),
    }),

  removeWhatsappNumber: (id: string) =>
    call<{ ok: boolean }>(`/api/whatsapp/${id}`, { method: 'DELETE' }),

  subscribeWhatsapp: (id: string) =>
    call<{ ok: boolean }>(`/api/whatsapp/${id}/subscribe`, { method: 'POST' }),

  refreshInbox: () =>
    call<{ pages: number; comments: number; messages: number }>('/api/inbox/refresh', {
      method: 'POST',
    }),

  /** The side list of the chat screen: one row per exchange, not per message. */
  conversations: (
    params: { pageId?: string; channel?: string; waiting?: boolean; before?: string } = {},
  ) => {
    const query = new URLSearchParams();
    if (params.pageId) query.set('pageId', params.pageId);
    if (params.channel) query.set('channel', params.channel);
    if (params.waiting) query.set('waiting', '1');
    if (params.before) query.set('before', params.before);

    return call<{
      conversations: ConversationDto[];
      waiting: number;
      nextBefore: string | null;
    }>(`/api/conversations${query.size > 0 ? `?${query}` : ''}`);
  },

  /** Everything said in one conversation, oldest first. */
  conversation: (id: string) =>
    call<{
      conversation: ConversationDto;
      items: Array<{
        id: string;
        message: string | null;
        authorName: string | null;
        fromPage: boolean;
        createdAt: string;
        repliedAt: string | null;
        externalCommentId: string;
        /** A reply of ours, read from our own record rather than from Meta. */
        ours?: boolean;
        suggestions: Array<{
          id: string;
          reply: string | null;
          createdAt: string;
          /** Which agent wrote it: the Page's own assistant, or a connected one. */
          source: 'assistant' | 'agent';
          sourceName: string | null;
        }>;
      }>;
    }>(`/api/conversations/${id}`),

  suggestInboxReply: (id: string) =>
    call<{ suggestion: InboxSuggestion }>(`/api/inbox/${id}/suggest`, {
      method: 'POST',
    }),

  replyToInboxItem: (
    id: string,
    message: string,
    from: { savedReplyId?: string; fromSuggestionId?: string } = {},
  ) =>
    call<{ ok: boolean }>(`/api/inbox/${id}/reply`, {
      method: 'POST',
      /*
       * Both origins are named rather than inferred from the text, for the
       * same reason and to opposite ends.
       *
       * A saved reply is named because an edited one is no longer that
       * sentence, and matching on text would miss the use. A draft is named
       * because an edited one is *exactly* the interesting case: the agent
       * wrote something, a person corrected it, and only the sender knows the
       * two are the same answer. Guessed from text, every correction would look
       * like a reply written from scratch — and the correction is the one thing
       * here worth learning from.
       */
      body: JSON.stringify({ message, ...from }),
    }),

  /** The sentences this tenant sends every day, in picker order. */
  savedReplies: () =>
    call<{
      replies: Array<{
        id: string;
        title: string;
        body: string;
        sortOrder: number;
        useCount: number;
        createdAt: string;
      }>;
    }>('/api/saved-replies'),

  createSavedReply: (input: { title: string; body: string }) =>
    call<{ id: string }>('/api/saved-replies', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  updateSavedReply: (id: string, input: { title?: string; body?: string; sortOrder?: number }) =>
    call<{ id: string }>(`/api/saved-replies/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),

  deleteSavedReply: (id: string) => call<void>(`/api/saved-replies/${id}`, { method: 'DELETE' }),

  dismissInboxItem: (id: string) =>
    call<{ ok: boolean }>(`/api/inbox/${id}/dismiss`, { method: 'POST' }),

  /**
   * Like a comment, or emoji-react to a message — whichever the item is.
   * `emoji` only matters for a message; Meta's Like edge on a comment has no
   * reaction to choose, so it is dropped there rather than refused.
   */
  reactToInboxItem: (id: string, emoji?: string) =>
    call<{ ok: boolean; channel: string }>(`/api/inbox/${id}/react`, {
      method: 'POST',
      body: JSON.stringify(emoji ? { emoji } : {}),
    }),

  removeInboxReaction: (id: string) =>
    call<{ ok: boolean; channel: string }>(`/api/inbox/${id}/react`, { method: 'DELETE' }),

  pageMentions: (pageId: string) =>
    call<{
      mentions: Array<{
        id: string;
        message: string | null;
        createdTime: string;
        authorName: string | null;
        permalink: string | null;
      }>;
      reviews: Array<{
        createdTime: string;
        positive: boolean;
        text: string | null;
        reviewerName: string | null;
      }>;
    }>(`/api/pages/${pageId}/mentions`),

  pageProfile: (pageId: string) => call<{ profile: PageProfile }>(`/api/pages/${pageId}/profile`),

  updatePageLocation: (pageId: string, location: Record<string, string | number>) =>
    call<{ profile: PageProfile }>(`/api/pages/${pageId}/location`, {
      method: 'PUT',
      body: JSON.stringify(location),
    }),

  updatePageHours: (
    pageId: string,
    hours: Array<{ day: string; open: string | null; close: string | null }>,
  ) =>
    call<{ profile: PageProfile }>(`/api/pages/${pageId}/hours`, {
      method: 'PUT',
      body: JSON.stringify({ hours }),
    }),

  updatePageImage: (pageId: string, kind: 'picture' | 'cover', file: File) => {
    const form = new FormData();
    form.set('kind', kind);
    form.set('file', file);
    // No content-type header: the browser has to set the multipart boundary.
    return call<{ profile: PageProfile }>(`/api/pages/${pageId}/image`, {
      method: 'POST',
      body: form,
    });
  },

  updatePageProfile: (pageId: string, patch: Record<string, string>) =>
    call<{ profile: PageProfile }>(`/api/pages/${pageId}/profile`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  syncPages: () =>
    call<{ discovered: number; pages: PageDto[] }>('/api/pages/sync', {
      method: 'POST',
    }),

  ownedPosts: (pageId: string, after?: string) =>
    call<{ posts: SourcePostDto[]; nextCursor: string | null }>(
      `/api/source/owned/posts?pageId=${encodeURIComponent(pageId)}${
        after ? `&after=${encodeURIComponent(after)}` : ''
      }`,
    ),

  publicSourcePages: () =>
    call<{
      pages: Array<{
        id: string;
        externalId: string;
        name: string;
        link: string | null;
      }>;
    }>('/api/source/public/pages'),

  addPublicSourcePage: (reference: string) =>
    call<{ page: { id: string; name: string } }>('/api/source/public/pages', {
      method: 'POST',
      body: JSON.stringify({ reference }),
    }),

  publicPosts: (publicSourcePageId: string) =>
    call<{ posts: SourcePostDto[]; nextCursor: string | null }>(
      `/api/source/public/posts?publicSourcePageId=${encodeURIComponent(publicSourcePageId)}`,
    ),

  uploadMedia: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return call<{ asset: { id: string; filename: string; byteSize: number } }>('/api/media', {
      method: 'POST',
      body: form,
    });
  },

  createComposition: (input: unknown) =>
    call<{ composition: { id: string; kind: string } }>('/api/source/compositions', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /* ---- Account (the caller's own tenant) ---- */

  /* ---- Forgotten password and sign-up (no session) ---- */

  requestPasswordReset: (email: string) =>
    call<{ ok: boolean; ttlSeconds: number }>('/api/auth/forgot/request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  completePasswordReset: (email: string, code: string, newPassword: string) =>
    call<{ ok: boolean }>('/api/auth/forgot/reset', {
      method: 'POST',
      body: JSON.stringify({ email, code, newPassword }),
    }),

  changeOwnPassword: (currentPassword: string, newPassword: string) =>
    call<{ ok: boolean }>('/api/auth/password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),

  requestSignup: (input: {
    organisationName: string;
    contactName: string;
    email: string;
    phone: string;
  }) =>
    call<{ ok: boolean; ttlSeconds: number }>('/api/signup/request', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  verifySignup: (email: string, code: string) =>
    call<{ ok: boolean; status: string }>('/api/signup/verify', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    }),

  /* ---- Sign-up review (super-administrator) ---- */

  adminSmtp: () => call<{ smtp: SmtpSummary }>('/api/admin/smtp'),

  updateSmtp: (input: {
    host: string;
    port: number;
    username?: string;
    password?: string;
    fromAddress: string;
    fromName?: string;
    isActive?: boolean;
  }) =>
    call<{ smtp: SmtpSummary }>('/api/admin/smtp', {
      method: 'PUT',
      body: JSON.stringify(input),
    }),

  testSmtp: (to: string) =>
    call<{ ok: boolean; error?: string }>('/api/admin/smtp/test', {
      method: 'POST',
      body: JSON.stringify({ to }),
    }),

  adminSignups: (status?: string) =>
    call<{ signups: SignupRequest[] }>(`/api/admin/signups${status ? `?status=${status}` : ''}`),

  approveSignup: (id: string, planCode: string, note?: string) =>
    call<{ ok: boolean }>(`/api/admin/signups/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ planCode, note }),
    }),

  rejectSignup: (id: string, note?: string) =>
    call<{ ok: boolean }>(`/api/admin/signups/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),

  account: () =>
    call<{
      tenant: {
        id: string;
        name: string;
        status: string;
        plan: { code: string; name: string };
      };
      usage: Record<string, QuotaUsage>;
      features: Record<string, FeatureState>;
      warnings: Array<{ resource: string; used: number; limit: number | null }>;
      role: string;
    }>('/api/account'),

  updateAccountSettings: (patch: {
    features?: Record<string, boolean>;
    quotaWarningPercent?: number;
  }) =>
    call<{ settings: unknown }>('/api/account/settings', {
      method: 'PUT',
      body: JSON.stringify(patch),
    }),

  users: () =>
    call<{
      users: Array<{
        id: string;
        email: string;
        displayName: string;
        role: string;
        isActive: boolean;
      }>;
    }>('/api/users'),

  createUser: (input: unknown) =>
    call<{ user: { id: string } }>('/api/users', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  updateUser: (id: string, patch: Record<string, unknown>) =>
    call<{ user: { id: string } }>(`/api/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    }),

  /** The pages one colleague looks after. */
  userPages: (id: string) => call<{ pageIds: string[] }>(`/api/users/${id}/pages`),

  /**
   * Replace that list wholesale.
   *
   * The whole set, not one page at a time: a page left out of the body is a
   * page taken away, which is what ticking boxes on a screen means.
   */
  setUserPages: (id: string, pageIds: string[]) =>
    call<{ pageIds: string[] }>(`/api/users/${id}/pages`, {
      method: 'PUT',
      body: JSON.stringify({ pageIds }),
    }),

  /**
   * A tenant's own Meta apps, plus the platform app they fall back to.
   *
   * The platform row is always present and is not editable — it is there so the
   * screen can name what a connection would otherwise go through silently.
   */
  metaApps: () =>
    call<{
      apps: Array<{
        id: string;
        label: string;
        provider: 'PLATFORM' | 'TENANT';
        externalAppId: string;
        loginConfigId: string | null;
        graphVersion: string | null;
        status: 'ACTIVE' | 'INVALID' | 'DISABLED';
        isDefault: boolean;
        verifiedAt: string | null;
        /** Meta's own words when the credentials did not check out. */
        verifyError: string | null;
        connectionCount: number;
        /** Paste this into the app dashboard's webhook configuration. */
        webhookCallbackUrl: string;
        createdAt: string;
      }>;
    }>('/api/meta-apps'),

  createMetaApp: (input: {
    label: string;
    externalAppId: string;
    appSecret: string;
    loginConfigId?: string;
    graphVersion?: string;
  }) => call<{ id: string }>('/api/meta-apps', { method: 'POST', body: JSON.stringify(input) }),

  updateMetaApp: (id: string, input: { label?: string; appSecret?: string }) =>
    call<{ id: string }>(`/api/meta-apps/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),

  deleteMetaApp: (id: string) => call<void>(`/api/meta-apps/${id}`, { method: 'DELETE' }),

  apiKeys: () =>
    call<{
      keys: Array<{
        id: string;
        name: string;
        prefix: string;
        /** The matrix, with Page names resolved by the server. */
        grants: Array<{
          pageScope: string;
          pageName: string | null;
          capability: ApiCapability;
          autoSend: boolean;
        }>;
        lastUsedAt: string | null;
        /** Null means it never expires. */
        expiresAt: string | null;
        revokedAt: string | null;
        /** False for a revoked key, and for one issued before keys were stored. */
        canReveal: boolean;
      }>;
    }>('/api/tenant/api-keys'),

  createApiKey: (input: { name: string; grants: ApiKeyGrantInput[]; expiresInDays?: number }) =>
    call<{
      key: { id: string; name: string; prefix: string; rawKey: string };
      notice: string;
    }>('/api/tenant/api-keys', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  revokeApiKey: (id: string) =>
    call<{ ok: boolean }>(`/api/tenant/api-keys/${id}`, { method: 'DELETE' }),

  /**
   * POST, not GET: showing a key is an act that is written to the audit log,
   * and a GET invites a prefetch to perform it without anybody deciding to.
   */
  revealApiKey: (id: string) =>
    call<{ rawKey: string; connectorUrl: string }>(`/api/tenant/api-keys/${id}/reveal`, {
      method: 'POST',
    }),

  /* ---- Platform administration (SUPERADMIN) ---- */

  adminStats: () =>
    call<{
      stats: {
        tenants: {
          total: number;
          active: number;
          suspended: number;
          cancelled: number;
        };
        users: number;
        connections: number;
        pages: number;
        jobs: {
          total: number;
          succeeded: number;
          partial: number;
          failed: number;
        };
        postsPublished: number;
        agentCallsThisMonth: number;
        repliesThisMonth: number;
        planDistribution: Array<{
          code: string;
          name: string;
          tenants: number;
        }>;
      };
    }>('/api/admin/stats'),

  adminTenants: () =>
    call<{
      tenants: Array<{
        id: string;
        name: string;
        slug: string;
        status: string;
        suspendedReason: string | null;
        plan: { code: string; name: string };
        counts: { users: number; connections: number; pages: number };
        createdAt: string;
      }>;
    }>('/api/admin/tenants'),

  /**
   * One tenant in full, users included.
   *
   * The console's only route to a user outside the operator's own tenant —
   * `/api/users` is scoped to the caller's. Without this the super-admin
   * password reset the server already permits has no way to name its target.
   */
  renameTenant: (id: string, patch: { name?: string; slug?: string }) =>
    call<{ ok: boolean }>(`/api/admin/tenants/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  /**
   * `confirmName` must equal the account's name; `force` is the second answer
   * for an account that still holds Pages or history. Without force the server
   * refuses with reason NOT_EMPTY and says what is inside.
   */
  deleteTenant: (id: string, confirmName: string, force = false) =>
    call<{
      ok: boolean;
      removed: {
        users: number;
        connections: number;
        pages: number;
        jobs: number;
      };
    }>(`/api/admin/tenants/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ confirmName, force }),
    }),

  /** Add a user to a tenant that is not your own. Super-admin only. */
  addTenantUser: (
    id: string,
    input: {
      email: string;
      displayName: string;
      password: string;
      role?: string;
    },
  ) =>
    call<{ user: { id: string } }>(`/api/admin/tenants/${id}/users`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  deleteUser: (id: string) => call<{ email: string }>(`/api/users/${id}`, { method: 'DELETE' }),

  adminTenant: (id: string) =>
    call<{
      tenant: {
        id: string;
        name: string;
        slug: string;
        status: string;
        users: Array<{
          id: string;
          email: string;
          displayName: string;
          role: string;
          isActive: boolean;
        }>;
      };
    }>(`/api/admin/tenants/${id}`),

  adminPlans: () => call<{ plans: AdminPlan[] }>('/api/admin/plans'),

  /** Which Meta permissions a customer's login dialog may ask for. */
  metaPermissions: () => call<{ permissions: MetaPermissionDto[] }>('/api/admin/meta-permissions'),

  setMetaPermissionStatus: (scope: string, status: MetaPermissionStatus, note?: string | null) =>
    call<{ permission: MetaPermissionDto }>(
      '/api/admin/meta-permissions/' + encodeURIComponent(scope),
      { method: 'PATCH', body: JSON.stringify({ status, note }) },
    ),

  adminCatalogue: () =>
    call<{ features: CatalogueFeature[]; quotas: CatalogueQuota[] }>('/api/admin/catalogue'),

  createTenant: (input: unknown) =>
    call<{ tenant: { id: string } }>('/api/admin/tenants', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  changeTenantPlan: (id: string, planCode: string) =>
    call<{ ok: boolean }>(`/api/admin/tenants/${id}/plan`, {
      method: 'POST',
      body: JSON.stringify({ planCode }),
    }),

  setTenantStatus: (id: string, status: string, reason?: string) =>
    call<{ ok: boolean }>(`/api/admin/tenants/${id}/status`, {
      method: 'POST',
      body: JSON.stringify({ status, reason }),
    }),

  updatePlan: (id: string, patch: Record<string, unknown>) =>
    call<{ plan: unknown }>(`/api/admin/plans/${id}`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    }),

  publishedPosts: () =>
    call<{
      posts: Array<{
        channel: string;
        postId: string;
        pageId: string;
        pageName: string;
        publishedAt: string;
        permalink: string;
        /** What the post says, so a row can be recognised without its id. */
        message: string | null;
        kind: string;
        imageUrls: string[];
        mediaIds: string[];
      }>;
    }>('/api/posts/published'),

  /**
   * One published post as Meta holds it now, with whatever Meta says is
   * narrowing who can see it.
   */
  postDetails: (postId: string, pageId: string) =>
    call<{
      details: {
        postId: string;
        pageId: string;
        pageName: string;
        message: string | null;
        createdTime: string | null;
        permalinkUrl: string | null;
        publishedAt: string | null;
        isPublished: boolean | null;
        isHidden: boolean | null;
        isExpired: boolean | null;
        privacy: { value: string | null; description: string | null } | null;
        targeting: unknown | null;
        targetingReadable: boolean;
        application: { id: string | null; name: string | null } | null;
        /** Empty means Meta reports nothing restricting it. */
        findings: Array<{ code: string; detail: string | null }>;
      };
    }>(`/api/posts/${encodeURIComponent(postId)}/details?pageId=${encodeURIComponent(pageId)}`),

  deletePost: (postId: string, pageId: string) =>
    call<{
      postId: string;
      pageName: string;
      ledgerReleased: boolean;
      notice: string;
    }>('/api/posts', {
      method: 'DELETE',
      body: JSON.stringify({ postId, pageId }),
    }),

  /** Meta gives no way to choose a reaction here — only a plain Like. */
  likePost: (postId: string, pageId: string) =>
    call<{ ok: boolean }>('/api/posts/like', {
      method: 'POST',
      body: JSON.stringify({ postId, pageId }),
    }),

  unlikePost: (postId: string, pageId: string) =>
    call<{ ok: boolean }>('/api/posts/like', {
      method: 'DELETE',
      body: JSON.stringify({ postId, pageId }),
    }),

  tenant: () =>
    call<{
      tenant: {
        id: string;
        name: string;
        status: string;
        plan: { code: string; name: string };
      };
      usage: Record<string, { used: number; limit: number }>;
    }>('/api/tenant'),

  /** Pages and the one agent each is bound to. */
  automationBindings: () => call<{ pages: PageBinding[] }>('/api/automation/bindings'),

  bindAgent: (
    pageId: string,
    input: {
      agentConfigId: string;
      autoReplyComments?: boolean;
      autoPublishReplies?: boolean;
      autoDraftInbox?: boolean;
      minCommentLength?: number;
      maxRepliesPerHour?: number;
    },
  ) =>
    call<{ ok: boolean }>(`/api/pages/${pageId}/automation`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),

  unbindAgent: (pageId: string) =>
    call<{ ok: boolean }>(`/api/pages/${pageId}/automation`, {
      method: 'DELETE',
    }),

  agents: () =>
    call<{
      agents: Array<{
        id: string;
        name: string;
        mode: string;
        /** Which agent to speak to. A name, not a place — see AutomationPanel. */
        externalAgentId: string | null;
        hasAuthToken: boolean;
        hasCallbackSecret: boolean;
        isActive: boolean;
      }>;
      automationEnabled: boolean;
      callbackUrlTemplate: string;
    }>('/api/agents'),

  createAgent: (input: unknown) =>
    call<{ id: string }>('/api/agents', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /**
   * Change one that exists.
   *
   * Omitted fields are left alone — most of all the credentials, which were
   * shown once and are gone. Correcting a name must never ask for a secret back.
   */
  updateAgent: (id: string, input: unknown) =>
    call<{ ok: boolean }>(`/api/agents/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),

  deleteAgent: (id: string) => call<{ ok: boolean }>(`/api/agents/${id}`, { method: 'DELETE' }),

  agentPerformance: (days: number) =>
    call<{
      windowDays: number;
      performance: Array<{
        agentConfigId: string;
        name: string;
        mode: string;
        totalCalls: number;
        succeeded: number;
        declined: number;
        empty: number;
        timedOut: number;
        failed: number;
        successRate: number;
        avgLatencyMs: number | null;
        p95LatencyMs: number | null;
        avgReplyChars: number | null;
      }>;
    }>(`/api/agents/performance?days=${days}`),

  setPageAutomation: (pageId: string, input: unknown) =>
    call<{ subscribed: boolean; subscriptionError: string | null }>(
      `/api/pages/${pageId}/automation`,
      { method: 'PUT', body: JSON.stringify(input) },
    ),

  automationQueue: (status?: string) =>
    call<{
      items: Array<{
        id: string;
        pageName: string;
        authorName: string | null;
        message: string | null;
        status: string;
        skipReason: string | null;
        agentReply: string | null;
        agentConfidence: number | null;
        publishedCommentId: string | null;
        receivedAt: string;
      }>;
    }>(`/api/automation/queue${status ? `?status=${status}` : ''}`),

  approveReply: (id: string, editedText?: string) =>
    call<{ status: string; detail: string }>(`/api/automation/queue/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ editedText }),
    }),

  dismissReply: (id: string, note?: string) =>
    call<{ ok: boolean }>(`/api/automation/queue/${id}/dismiss`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),

  commentCapabilities: () =>
    call<{
      repliesEnabled: boolean;
      maxCommentsPerBatch: number;
      pacingMs: number;
    }>('/api/comments/capabilities'),

  postComments: (sourcePostId: string) =>
    call<{
      comments: Array<{
        id: string;
        message: string | null;
        createdTime: string;
        authorName: string | null;
        fromPage: boolean;
        canReply: boolean;
      }>;
    }>(`/api/comments?sourcePostId=${encodeURIComponent(sourcePostId)}`),

  createCommentJob: (input: unknown) =>
    call<{
      jobId: string;
      queuedComments: number;
      pageName: string;
      pacingMs: number;
      estimatedCompletionMs: number;
    }>('/api/comments/jobs', { method: 'POST', body: JSON.stringify(input) }),

  createJob: (input: unknown) =>
    call<{
      jobId: string;
      queuedTargets: number;
      /** Null when it went out immediately, which is the default. */
      scheduledAt: string | null;
      skipped: Array<{ pageId: string; reason: string }>;
    }>('/api/jobs', { method: 'POST', body: JSON.stringify(input) }),

  /** Call back a scheduled job. Refused once a worker has it. */
  cancelJob: (id: string) =>
    call<{ cancelled: number; couldNotStop: number }>(`/api/jobs/${id}/cancel`, {
      method: 'POST',
    }),

  jobs: (
    params: {
      view?: string;
      pageId?: string;
      socialAccountId?: string;
      channel?: string;
      includeHidden?: boolean;
      /** Days, inclusive of both ends. The server widens `to` to cover its day. */
      from?: string;
      to?: string;
      sort?: 'asc' | 'desc';
    } = {},
  ) => {
    const query = new URLSearchParams();
    if (params.view) query.set('view', params.view);
    if (params.pageId) query.set('pageId', params.pageId);
    if (params.socialAccountId) query.set('socialAccountId', params.socialAccountId);
    if (params.channel) query.set('channel', params.channel);
    if (params.includeHidden) query.set('includeHidden', '1');
    if (params.from) query.set('from', params.from);
    if (params.to) query.set('to', params.to);
    if (params.sort) query.set('sort', params.sort);

    return call<{
      jobs: JobDto[];
      total: number;
      /** One number per tab, counted by the server from the same filters. */
      counts: Record<string, number>;
      /** Whether this person may reveal hidden rows, so the control can be shown. */
      maySeeHidden: boolean;
    }>(`/api/jobs${query.size > 0 ? `?${query}` : ''}`);
  },

  editJob: (
    id: string,
    patch: {
      message?: string;
      link?: string | null;
      firstComment?: string | null;
      /** The whole list of attached files, in order. */
      mediaAssetIds?: string[];
      scheduledAt?: string;
    },
  ) =>
    call<{ scheduledAt: string | null }>(`/api/jobs/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  hideJob: (id: string) =>
    call<{ hiddenAt: string | null }>(`/api/jobs/${id}/hide`, { method: 'POST' }),

  revealJob: (id: string) =>
    call<{ hiddenAt: string | null }>(`/api/jobs/${id}/reveal`, { method: 'POST' }),

  job: (id: string) =>
    call<{
      job: JobDto;
      attempts: Array<{
        targetPageName: string;
        channel: string;
        attempt: number;
        succeeded: boolean;
        endpoint: string;
        httpStatus: number | null;
        metaErrorCode: number | null;
        metaErrorSubcode: number | null;
        metaErrorMessage: string | null;
        fbtraceId: string | null;
        resultingPostId: string | null;
        retryable: boolean;
        createdAt: string;
      }>;
    }>(`/api/jobs/${id}`),

  retryJob: (id: string) =>
    call<{ requeued: number; untouched: number }>(`/api/jobs/${id}/retry`, {
      method: 'POST',
    }),

  resolveTarget: (targetId: string, foundPostId: string | null) =>
    call<{ status: string }>(`/api/jobs/targets/${targetId}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ foundPostId }),
    }),

  apiCalls: (onlyErrors: boolean) =>
    call<{
      calls: Array<{
        id: string;
        method: string;
        path: string;
        httpStatus: number | null;
        ok: boolean;
        durationMs: number | null;
        metaErrorCode: number | null;
        metaErrorMessage: string | null;
        fbtraceId: string | null;
        createdAt: string;
      }>;
    }>(`/api/logs/api-calls?limit=100${onlyErrors ? '&onlyErrors=true' : ''}`),

  auditLog: () =>
    call<{
      entries: Array<{
        id: string;
        action: string;
        entity: string;
        actor: string;
        detail: unknown;
        createdAt: string;
      }>;
    }>('/api/logs/audit'),

  rateLimit: () =>
    call<{ usage: Record<string, number | undefined>; paused: boolean }>('/api/logs/rate-limit'),

  /* ---- Content generation studio ---- */

  listGenerationConnectors: () =>
    call<{ connectors: GenerationConnector[] }>('/api/generation/connectors'),

  createGenerationConnector: (input: GenerationConnectorInput) =>
    call<{ connector: GenerationConnector }>('/api/generation/connectors', {
      method: 'POST',
      body: JSON.stringify(input),
    }).then((r) => r.connector),

  /** Omitted `authToken` leaves the stored one unchanged. */
  updateGenerationConnector: (
    id: string,
    input: Partial<GenerationConnectorInput & { isActive: boolean }>,
  ) =>
    call<{ connector: GenerationConnector }>(`/api/generation/connectors/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }).then((r) => r.connector),

  /** Usage and estimated cost over a period (dates as YYYY-MM-DD); see GenerationStatsDto. */
  generationStats: (params: { from?: string; to?: string; groupBy?: 'day' | 'week' | 'month' }) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) if (value) query.set(key, value);
    const suffix = query.toString();
    return call<GenerationStatsDto>(`/api/generation/stats${suffix ? `?${suffix}` : ''}`);
  },

  deleteGenerationConnector: (id: string) =>
    call<void>(`/api/generation/connectors/${id}`, { method: 'DELETE' }),

  /** Starts a real (billed) generation on the connector; poll getGenerationConnectorTest. */
  testGenerationConnector: (
    id: string,
    input: { prompt?: string; referenceMediaIds?: string[]; durationSeconds?: number } = {},
  ) =>
    call<{ jobId: string; alreadyRunning: boolean }>(`/api/generation/connectors/${id}/test`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  getGenerationConnectorTest: (id: string) =>
    call<GenerationConnectorTestState>(`/api/generation/connectors/${id}/test`),

  /** Asks a text model to propose corrections for a connector whose test failed. Saves nothing. */
  repairGenerationConnector: (id: string, input: { hint?: string } = {}) =>
    call<GenerationConnectorRepair>(`/api/generation/connectors/${id}/repair`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  uploadGenerationMedia: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return call<{ asset: { id: string; filename: string; mimeType: string; byteSize: number } }>(
      '/api/generation/media',
      { method: 'POST', body: form },
    );
  },

  createGenerationJob: (input: {
    capability: GenerationCapability;
    promptText?: string;
    referenceMediaIds?: string[];
    audioMediaId?: string;
    parentJobId?: string;
    connectorId?: string;
    feedbackText?: string;
    /** The project this request belongs to; omitted, the server opens a new one. */
    projectId?: string;
  }) =>
    call<{ job: GenerationJob }>('/api/generation/jobs', {
      method: 'POST',
      body: JSON.stringify(input),
    }).then((r) => r.job),

  listGenerationJobs: (params: { capability?: GenerationCapability; status?: GenerationJobStatus } = {}) => {
    const query = new URLSearchParams();
    if (params.capability) query.set('capability', params.capability);
    if (params.status) query.set('status', params.status);
    return call<{ jobs: GenerationJob[] }>(
      `/api/generation/jobs${query.size > 0 ? `?${query}` : ''}`,
    );
  },

  getGenerationJob: (id: string) =>
    call<{ job: GenerationJob }>(`/api/generation/jobs/${id}`).then((r) => r.job),

  shareGenerationJob: (id: string) =>
    call<{ url: string; expiresAt: string }>(`/api/generation/jobs/${id}/share`, { method: 'POST' }),

  approveGenerationJob: (id: string) =>
    call<{ job: GenerationJob }>(`/api/generation/jobs/${id}/approve`, { method: 'POST' }).then(
      (r) => r.job,
    ),

  reviseGenerationJob: (
    id: string,
    input: {
      feedbackText: string;
      promptText?: string;
      referenceMediaIds?: string[];
      audioMediaId?: string;
      projectId?: string;
    },
  ) =>
    call<{ job: GenerationJob }>(`/api/generation/jobs/${id}/revise`, {
      method: 'POST',
      body: JSON.stringify(input),
    }).then((r) => r.job),

  /* Projects: one request and everything made from it. */

  listGenerationProjects: (params: { limit?: number; cursor?: string | null } = {}) => {
    const query = new URLSearchParams();
    if (params.limit) query.set('limit', String(params.limit));
    if (params.cursor) query.set('cursor', params.cursor);
    return call<{ projects: GenerationProjectSummaryDto[]; nextCursor: string | null }>(
      `/api/generation/projects${query.size > 0 ? `?${query}` : ''}`,
    );
  },

  createGenerationProject: (input: Pick<CreateGenerationProjectInput, 'requestText'> & Partial<CreateGenerationProjectInput>) =>
    call<{ project: GenerationProject }>('/api/generation/projects', {
      method: 'POST',
      body: JSON.stringify(input),
    }).then((r) => r.project),

  /** Which connector each step of a request would use, before anything is created. */
  previewGenerationRoute: (input: PreviewRouteInput) =>
    call<{ steps: GenerationRouteStep[] }>('/api/generation/route-preview', {
      method: 'POST',
      body: JSON.stringify(input),
    }).then((r) => r.steps),

  /**
   * Turns a finished media prompt into the picture, voice or video. A refusal
   * is a 400 whose message says why (a connector's longest clip, say).
   */
  produceGenerationProject: (id: string, input: { fromJobId?: string; connectorId?: string } = {}) =>
    call<GenerationProduceResult>(`/api/generation/projects/${id}/produce`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  listGenerationOptionPresets: () =>
    call<{ presets: GenerationOptionPreset[] }>('/api/generation/option-presets').then((r) => r.presets),

  saveGenerationOptionPreset: (input: SaveOptionPresetInput) =>
    call<{ preset: GenerationOptionPreset }>('/api/generation/option-presets', {
      method: 'POST',
      body: JSON.stringify(input),
    }).then((r) => r.preset),

  deleteGenerationOptionPreset: (id: string) =>
    call<{ ok: true }>(`/api/generation/option-presets/${id}`, { method: 'DELETE' }),

  getGenerationProject: (id: string) =>
    call<{ project: GenerationProject }>(`/api/generation/projects/${id}`).then((r) => r.project),

  /** Archives it: the project leaves the list; its files are not deleted. */
  archiveGenerationProject: (id: string) =>
    call<{ ok: true }>(`/api/generation/projects/${id}`, { method: 'DELETE' }),

  /** Tell the project its output went to publishing. Sent after the post is saved. */
  recordGenerationPublication: (
    id: string,
    input: { publishJobId: string; mode: 'NEW_POST' | 'ATTACHED'; mediaIds: string[] },
  ) =>
    call<{ publication: GenerationProjectPublicationDto }>(
      `/api/generation/projects/${id}/publications`,
      { method: 'POST', body: JSON.stringify(input) },
    ).then((r) => r.publication),

  /* Video plans: a script cut into shots, run one paid VIDEO call at a time. */

  /** Asks the SCRIPT connector to write the shot list; takes 5–30 s. */
  createVideoPlan: (input: {
    scriptText?: string;
    scriptJobId?: string;
    referenceMediaIds?: string[];
    targetSeconds?: number;
    pauseBetweenShots?: boolean;
    projectId?: string;
  }) =>
    call<{ plan: VideoPlan }>('/api/generation/video-plans', {
      method: 'POST',
      body: JSON.stringify(input),
    }).then((r) => r.plan),

  listVideoPlans: () =>
    call<{ plans: VideoPlan[] }>('/api/generation/video-plans').then((r) => r.plans),

  getVideoPlan: (id: string) =>
    call<{ plan: VideoPlan }>(`/api/generation/video-plans/${id}`).then((r) => r.plan),

  /** Sending `shots` replaces the list; the server resets from the first changed shot on. */
  updateVideoPlan: (
    id: string,
    input: { shots?: VideoPlanShotInput[]; pauseBetweenShots?: boolean; referenceMediaIds?: string[] },
  ) =>
    call<{ plan: VideoPlan }>(`/api/generation/video-plans/${id}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }).then((r) => r.plan),

  /** Starts, or continues after PAUSED/FAILED. Every shot is a paid call. */
  runVideoPlan: (id: string) =>
    call<{ plan: VideoPlan }>(`/api/generation/video-plans/${id}/run`, { method: 'POST' }).then(
      (r) => r.plan,
    ),

  /** Redo one shot (and everything after it), optionally with a new description. */
  resetVideoPlanShot: (id: string, shotId: string, input: { visualPrompt?: string } = {}) =>
    call<{ plan: VideoPlan }>(`/api/generation/video-plans/${id}/shots/${shotId}/reset`, {
      method: 'POST',
      body: JSON.stringify(input),
    }).then((r) => r.plan),

  shareVideoPlan: (id: string) =>
    call<{ url: string; expiresAt: string }>(`/api/generation/video-plans/${id}/share`, {
      method: 'POST',
    }),
};

/**
 * Where to fetch or play a generation result's file.
 *
 * A generation result is stored as an ordinary `MediaAsset` row, so the
 * existing session-scoped `GET /api/media/:id` (media.ts) already serves it —
 * no new route needed. `GET /api/generation/media/:id/download` is a
 * different thing: an HMAC-signed, session-less link handed to an *external*
 * provider so it can fetch a reference/audio file across the network. The
 * dashboard is already authenticated by cookie, so it uses the plain route.
 */
export function mediaAssetUrl(job: GenerationJob, field: 'resultMediaId' | 'audioMediaId'): string | null {
  const id = job[field];
  return id ? `/api/media/${id}` : null;
}

/**
 * The same file as an attachment: `?download=1` makes the server answer with
 * `Content-Disposition: attachment` and a name that carries its extension, so
 * the browser saves it as a file rather than opening it.
 */
export function mediaDownloadUrl(mediaAssetId: string): string {
  return `/api/media/${mediaAssetId}?download=1`;
}
